import type { Control, FieldErrors, FieldValues, Path, UseFormRegister } from "react-hook-form";
import { useFieldArray, type ArrayPath } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";

type FaqFieldError = { question?: { message?: string }; answer?: { message?: string } };

interface FaqItemsCardProps<T extends FieldValues> {
  control: Control<T>;
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
}

export function FaqItemsCard<T extends FieldValues>({
  control,
  register,
  errors,
}: FaqItemsCardProps<T>) {
  const faqItems = useFieldArray({
    control,
    name: "faqItems" as ArrayPath<T>,
  });

  const faqErrors = (errors as { faqItems?: FaqFieldError[] }).faqItems ?? [];
  const path = (value: string) => value as Path<T>;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>FAQ da campanha</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            Perguntas customizadas para esta campanha. Se vazio, o front usa o FAQ padrão da mecânica.
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => faqItems.append({ question: "", answer: "" } as never)}>
          <Plus className="mr-2 h-4 w-4" />
          Adicionar pergunta
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {faqItems.fields.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/30 px-4 py-5 text-sm text-muted-foreground">
            Nenhuma pergunta customizada. Clique em “Adicionar pergunta” para criar um FAQ próprio para a campanha.
          </div>
        ) : null}

        <div className="space-y-4">
          {faqItems.fields.map((field, index) => (
            <div key={field.id} className="rounded-xl border bg-card p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-ink">Pergunta {index + 1}</p>
                  <p className="text-xs text-muted-foreground">O conteúdo preenchido aqui substitui o FAQ padrão no front público.</p>
                </div>
                <Button type="button" variant="ghost" size="icon" onClick={() => faqItems.remove(index)} aria-label={`Remover pergunta ${index + 1}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor={`faq-question-${index}`}>Pergunta</Label>
                  <Input
                    id={`faq-question-${index}`}
                    placeholder="Ex.: Como participo da campanha?"
                    {...register(path(`faqItems.${index}.question`))}
                  />
                  {faqErrors[index]?.question?.message ? (
                    <p className="mt-1 text-sm text-red-500">{faqErrors[index]?.question?.message}</p>
                  ) : null}
                </div>

                <div>
                  <Label htmlFor={`faq-answer-${index}`}>Resposta</Label>
                  <Textarea
                    id={`faq-answer-${index}`}
                    placeholder="Explique a regra, prazo ou condição..."
                    className="min-h-28"
                    {...register(path(`faqItems.${index}.answer`))}
                  />
                  {faqErrors[index]?.answer?.message ? (
                    <p className="mt-1 text-sm text-red-500">{faqErrors[index]?.answer?.message}</p>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
